// nightRoles.ts
//
// The setup sheet: the things a guild decides ONCE and then repeats all night.
// Aelisi main tanks every pull. Cowdealer innervates the same mage every time.
// Crondex has Curse of Elements on every boss.
//
// Three ideas, in the order they resolve:
//
//   1. DEPTH CHART.  A guild ranks its own warlocks, mages and hunters, and
//      that one ranking drives every ordinal duty that class does. The best
//      warlock takes Curse of Elements, Soulstone 1 AND the warlock tanking
//      job, because they are all "Warlock #1". Reorder the chart once and all
//      three move together. This is why the sheet's Curses, Soulstones and
//      Mages boxes always list the same people in the same order.
//
//   2. NIGHT ROLE.  A named job, set by hand or auto-filled: Main Tank, Off
//      Tank, Main Tank Healer, Raid Healer. Roles that are ordinal within a
//      class read straight off the depth chart instead of picking their own.
//
//   3. PER-SLOT OVERRIDE (in assignmentModel).  One boss, one exception,
//      leaving the night role untouched everywhere else.

import {
  Raider, RosterSnapshot, SlotRule, SlotTarget,
  closestMatch, isEligible, rulePreference,
} from "./assignmentModel";

// ── Depth charts ─────────────────────────────────────────────
export type DepthOrder = Record<string, string[]>;   // className -> characterId[]

// The chart the board actually uses: whatever the lead has ordered by hand,
// then everyone else in that class appended alphabetically so a new recruit
// slots in at the bottom instead of vanishing.
//
// Charted classes come from the roster rather than a list of class names. The
// old fixed list was game data, and it was doing nothing a guild with one
// warlock would notice anyway: a chart of one person is hidden by the UI.
export function resolveDepth(roster: Raider[], manual: DepthOrder): DepthOrder {
  const out: DepthOrder = {};
  const present: string[] = [];
  roster.forEach((r) => {
    if (!r.isBench && present.indexOf(r.className) < 0) present.push(r.className);
  });
  present.sort().forEach((cls) => {
    const members = roster
      .filter((r) => r.className === cls && !r.isBench)
      .sort((a, b) => a.name.localeCompare(b.name));
    const byId: Record<string, boolean> = {};
    members.forEach((m) => { byId[m.characterId] = true; });

    const ordered: string[] = [];
    (manual[cls] || []).forEach((id) => {
      if (byId[id] && ordered.indexOf(id) < 0) ordered.push(id);
    });
    members.forEach((m) => {
      if (ordered.indexOf(m.characterId) < 0) ordered.push(m.characterId);
    });
    if (ordered.length > 0) out[cls] = ordered;
  });
  return out;
}

export const depthPick = (
  depth: DepthOrder, className: string, index: number,
): string | null => (depth[className] || [])[index] || null;

// ── Night roles ──────────────────────────────────────────────
// The catalogue itself lives in sheet_night_role / sheet_night_group and
// arrives with the sheet config. It used to be a 60-line array in this file,
// which meant a guild could not add "Fear Ward on the MT" without a deploy,
// and a second game version could not exist at all.
export type NightGroupKey = string;

export type NightGroup = {
  key: NightGroupKey;
  label: string;
  note?: string;
};

export type NightRole = {
  key: string;
  group: NightGroupKey;
  label: string;
  short: string;
  marker?: number | null;
  rule: SlotRule;
  // Ordinal within a class: reads position `index` of that class's depth
  // chart rather than picking independently.
  depth?: { className: string; index: number };
  target?: SlotTarget;
  note?: string;
};

export const nightRole = (roles: NightRole[], key: string) =>
  roles.filter((r) => r.key === key)[0] || null;

export const rolesInGroup = (roles: NightRole[], g: NightGroupKey) =>
  roles.filter((r) => r.group === g);

// What the lead has set by hand. An empty string means "deliberately nobody",
// which is different from the key being absent.
export type RoleAssignments = Record<string, string>;
export type RoleTargets = Record<string, string>;

export type RoleSource = "auto" | "depth" | "manual" | "replaced";

export type ResolvedRole = {
  role: NightRole;
  raider: Raider | null;
  source: RoleSource;
  // Who the carried-over sheet named, when that person is not in this raid.
  replacedName?: string | null;
  target: string | null;
  targetRaider: Raider | null;
  targetManual: boolean;
};

// Resolve every night role ONCE, so a decision made here reaches every boss.
export function resolveNightRoles(
  roles: NightRole[],
  roster: Raider[],
  assignments: RoleAssignments,
  targets: RoleTargets,
  depth: DepthOrder,
  snapshot: RosterSnapshot = {},
): Record<string, ResolvedRole> {
  const out: Record<string, ResolvedRole> = {};
  const byId: Record<string, Raider> = {};
  roster.forEach((r) => { byId[r.characterId] = r; });

  const usedInGroup: Record<string, Record<string, boolean>> = {};
  const load: Record<string, number> = {};
  const claim = (group: string, id: string) => {
    usedInGroup[group] = usedInGroup[group] || {};
    usedInGroup[group][id] = true;
    load[id] = (load[id] || 0) + 1;
  };

  // Hand-set picks are claimed first so auto-fill never steals them.
  roles.forEach((role) => {
    const set = assignments[role.key];
    if (set) claim(role.group, set);
  });

  roles.forEach((role) => {
    let raider: Raider | null = null;
    let source: RoleSource = "auto";
    let replacedName: string | null = null;

    const set = assignments[role.key];
    if (set !== undefined) {
      source = "manual";
      raider = set ? byId[set] || null : null;
      // Carried over from last week and that person did not sign this time.
      // Empty is the wrong answer: pick the nearest body and say so, which is
      // what makes "copy the sheet forward" survive a changing roster.
      if (set && !raider) {
        const hint = snapshot[set] || null;
        replacedName = hint ? hint.name : null;
        const group = usedInGroup[role.group] = usedInGroup[role.group] || {};
        const free = roster.filter((r) =>
          isEligible(r, role.rule) && !group[r.characterId]);
        raider = closestMatch(
          free.length > 0 ? free : roster.filter((r) => isEligible(r, role.rule)),
          hint, role.rule, load);
        source = "replaced";
        if (raider) claim(role.group, raider.characterId);
      }
    } else if (role.depth) {
      // The whole point of the depth chart: no independent choice here.
      const id = depthPick(depth, role.depth.className, role.depth.index);
      raider = id ? byId[id] || null : null;
      source = "depth";
      if (raider) claim(role.group, raider.characterId);
    } else {
      const eligible = roster.filter((r) => isEligible(r, role.rule));
      const group = usedInGroup[role.group] = usedInGroup[role.group] || {};
      const fresh = eligible.filter((r) => !group[r.characterId]);
      const pool = fresh.length > 0 ? fresh : eligible;
      raider = pool.slice().sort((a, b) => {
        const pa = rulePreference(a, role.rule);
        const pb = rulePreference(b, role.rule);
        if (pa !== pb) return pa - pb;
        const la = load[a.characterId] || 0;
        const lb = load[b.characterId] || 0;
        if (la !== lb) return la - lb;
        return a.name.localeCompare(b.name);
      })[0] || null;
      if (raider) claim(role.group, raider.characterId);
    }

    let target: string | null = null;
    let targetRaider: Raider | null = null;
    let targetManual = false;
    if (role.target) {
      const setT = targets[role.key];
      if (setT !== undefined) {
        target = setT || null;
        targetManual = true;
      } else if (role.target.kind === "text") {
        target = role.target.def != null ? role.target.def : (role.target.options[0] || null);
      } else if (role.target.roleRef && out[role.target.roleRef]) {
        const ref = out[role.target.roleRef].raider;
        target = ref ? ref.characterId : null;
      }
      if (role.target.kind === "raider" && target) targetRaider = byId[target] || null;
    }

    out[role.key] = {
      role, raider, source, replacedName, target, targetRaider, targetManual,
    };
  });

  // Raider targets with no roleRef and nothing set fall back to auto-fill,
  // spread out so three innervates do not all land on the same mage.
  const taken: Record<string, boolean> = {};
  roles.forEach((role) => {
    const r = out[role.key];
    if (!role.target || role.target.kind !== "raider" || r.target) return;
    const rule = role.target.rule;
    const picked = roster.filter((x) =>
      isEligible(x, rule) && !taken[x.characterId]
      && (!r.raider || x.characterId !== r.raider.characterId))[0] || null;
    if (picked) {
      taken[picked.characterId] = true;
      r.target = picked.characterId;
      r.targetRaider = picked;
    }
  });

  return out;
}

// Per-boss templates live in encounters.ts, which imports this module for
// its role bindings. Plan construction lives there too, for the same
// no-import-cycle reason this file never reaches back into assignmentModel.
